import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Loader2, CornerDownLeft, Command } from "lucide-react";

const COMMANDS = [
  { id: "dashboard", label: "Jump to Command Center", view: "home" },
  { id: "cases", label: "Open All Cases / Referrals", action: "cases" },
  { id: "telehealth", label: "Schedule Telehealth / Medical Records", action: "telehealth" },
  { id: "demand", label: "Generate Demand / Litigation Command", view: "litigation" },
  { id: "automation", label: "Client Automation Rules", view: "automation" },
  { id: "drone", label: "Dispatch Drone / Drone Command", view: "drone" },
  { id: "financial", label: "Financial Command", view: "financial" },
  { id: "acquisition", label: "Client Acquisition Command", view: "acquisition" },
  { id: "autoaccept", label: "Auto-Accept Settings", action: "autoaccept" },
  { id: "billing", label: "Billing & Membership", action: "billing" },
];

export const CommandBar = ({ open, onClose, onNavigate }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (open) { setQ(""); setResults([]); setTimeout(() => ref.current?.focus(), 50); } }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get(`/firm/command/search?q=${encodeURIComponent(q)}`)
        .then(({ data }) => setResults(data.results || [])).catch(() => setResults([])).finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (!open) return null;
  const cmds = COMMANDS.filter((c) => !q.trim() || c.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={onClose} data-testid="command-bar-overlay">
      <div className="w-full max-w-xl bg-[#0E1624] border border-[#1E2C45] rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="command-bar">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1E2C45]">
          <Search className="w-4 h-4 text-slate-500" />
          <input ref={ref} value={q} onChange={(e) => setQ(e.target.value)} data-testid="command-bar-input"
            placeholder="Search cases, clients, providers, records, exports… or type a command"
            className="flex-1 bg-transparent text-sm text-slate-200 placeholder:text-slate-500 outline-none" />
          {busy && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
          <kbd className="text-[9px] text-slate-500 border border-[#1E2C45] rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {results.length > 0 && <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Results</div>}
          {results.map((r) => (
            <button key={`${r.type}-${r.id}`} onClick={() => { onNavigate({ result: r }); onClose(); }} data-testid={`command-result-${r.type}-${r.id}`}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-sm text-slate-300 hover:bg-[#152033]">
              <span className="truncate">{r.label}<span className="text-slate-500 text-xs ml-2">{r.sub}</span></span>
              <span className="text-[9px] uppercase font-bold text-blue-400 shrink-0">{r.type}</span>
            </button>
          ))}
          <div className="px-2 pt-2 pb-0.5 text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Quick Commands</div>
          {cmds.map((c) => (
            <button key={c.id} onClick={() => { onNavigate(c); onClose(); }} data-testid={`command-cmd-${c.id}`}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-sm text-slate-300 hover:bg-[#152033]">
              <span className="flex items-center gap-2"><Command className="w-3.5 h-3.5 text-slate-500" /> {c.label}</span>
              <CornerDownLeft className="w-3 h-3 text-slate-600" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandBar;
